<template>
  <div class="grid gap-32">
    <p class="text-[--raw-verydarkgrey]">
      {{ i18n(translations.intro) }}
    </p>

    <section v-for="group in GROUPS" :key="group.id" class="grid gap-12">
      <h3 class="raw-cta-2">{{ i18n(group.title) }}</h3>

      <div class="grid gap-4">
        <MetalAccordion
          v-for="person in group.people"
          :key="person.name"
          sticky
          :open="open[group.id] === person.name"
          :group-open="open[group.id] !== null"
          @toggle="(isOpen) => (open[group.id] = isOpen ? person.name : null)"
        >
          <template #header>
            <span
              class="flex flex-col gap-x-8 gap-y-4 text-left md:flex-row md:flex-wrap md:items-baseline"
            >
              <span class="raw-cta-2">{{ person.name }}</span>
              <span class="raw-body-3 text-[--raw-darkgrey]">{{
                i18n(person.role)
              }}</span>
            </span>
          </template>
          <p
            v-for="paragraph in person.bio"
            :key="paragraph.id"
            class="text-[--raw-verydarkgrey]"
          >
            <InlineMarkdown :message="paragraph" />
          </p>
        </MetalAccordion>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import InlineMarkdown from '~/scopes/campaign/components/UI/InlineMarkdown.vue'
import MetalAccordion from '~/scopes/campaign/components/highlights/metal/MetalAccordion.vue'
import type { I18nDescriptor } from '~/scopes/campaign/data/metals/types'

import translations from './ContributorsDoc.translations'

// Names are plain strings — proper nouns don't translate — while roles are prose and
// go through i18n.
const i18n = useI18n()

type GroupId = 'experts' | 'authors'

interface Contributor {
  name: string
  role: I18nDescriptor
  bio: readonly I18nDescriptor[]
}

interface Group {
  id: GroupId
  title: I18nDescriptor
  people: readonly Contributor[]
}

// The one person the client lists in both groups; one object, so the bios can't drift.
const DIAZ_LEVI: Contributor = {
  name: 'Paula Díaz Levi',
  role: translations.diazLeviRole,
  bio: [translations.diazLeviBio],
}

// Both rosters in the client's order, which is theirs to set — nothing is sorted here.
const GROUPS: readonly Group[] = [
  {
    id: 'experts',
    title: translations.expertsTitle,
    people: [
      {
        name: 'Gisele Azimi',
        role: translations.azimiRole,
        bio: [
          translations.azimiBio1,
          translations.azimiBio2,
          translations.azimiBio3,
        ],
      },
      {
        name: 'Vince Beiser',
        role: translations.beiserRole,
        bio: [
          translations.beiserBio1,
          translations.beiserBio2,
          translations.beiserBio3,
          translations.beiserBio4,
          translations.beiserBio5,
        ],
      },
      DIAZ_LEVI,
      {
        name: 'Sandra Goldmark',
        role: translations.goldmarkRole,
        bio: [translations.goldmarkBio],
      },
      {
        name: 'David Maenda Kithoko',
        role: translations.maendaKithokoRole,
        bio: [
          translations.maendaKithokoBio1,
          translations.maendaKithokoBio2,
          translations.maendaKithokoBio3,
          translations.maendaKithokoBio4,
          translations.maendaKithokoBio5,
          translations.maendaKithokoBio6,
        ],
      },
      {
        name: 'Edson Krenak',
        role: translations.krenakRole,
        bio: [translations.krenakBio],
      },
      {
        name: 'Mark Maslin',
        role: translations.maslinRole,
        bio: [translations.maslinBio],
      },
      {
        name: 'Eric Ngang',
        role: translations.ngangRole,
        bio: [translations.ngangBio1, translations.ngangBio2],
      },
      {
        name: 'Marie Perrin',
        role: translations.perrinRole,
        bio: [translations.perrinBio1, translations.perrinBio2],
      },
    ],
  },
  {
    id: 'authors',
    title: translations.authorsTitle,
    people: [
      {
        name: 'Noel Lyn Smith',
        role: translations.smithRole,
        bio: [translations.smithBio],
      },
      {
        name: 'Sam Geall',
        role: translations.geallRole,
        bio: [translations.geallBio],
      },
      {
        name: 'Caleb Larson',
        role: translations.larsonRole,
        bio: [translations.larsonBio],
      },
      {
        name: 'Sergio Mendoza',
        role: translations.mendozaRole,
        bio: [translations.mendozaBio],
      },
      {
        name: 'Hannah JD Davies',
        role: translations.daviesRole,
        bio: [translations.daviesBio],
      },
      {
        name: 'Albert Anseh',
        role: translations.oppongAnsahRole,
        bio: [translations.oppongAnsahBio],
      },
      DIAZ_LEVI,
      {
        name: 'Thomas Hobbs',
        role: translations.hobbsRole,
        bio: [translations.hobbsBio],
      },
      {
        name: 'India Blue van Spall',
        role: translations.vanSpallRole,
        bio: [translations.vanSpallBio1, translations.vanSpallBio2],
      },
    ],
  },
]

// Single-open, keyed by name — per group, not per person, since Paula is in both.
const open = ref<Record<GroupId, string | null>>({
  experts: null,
  authors: null,
})
</script>
